import { Skeleton } from "@bafe/ui";
import { useEffect } from "react";
import { Navigate, useLocation } from "react-router";

import { signInNavigateTarget } from "../lib/auth-flow.js";
import { isUnauthenticated } from "../lib/errors";
import {
	isBlockedForMailOnlyMember,
	isMailOnlyOrganisationRole,
	WEBMAIL_HOME,
} from "../lib/mail-only-member.js";
import { activeMembership, useSession } from "../lib/session";
import { useOrganisationStore } from "../lib/stores";
import { PlatformShell } from "./PlatformShell";
import { ErrorPanel } from "./ErrorPanel";

export function RequireAuth() {
	const session = useSession();
	const location = useLocation();
	const organisationId = useOrganisationStore((state) => state.organisationId);
	const setOrganisationId = useOrganisationStore((state) => state.setOrganisationId);

	const data = session.data;

	useEffect(() => {
		if (!data) return;
		const owned = data.memberships.map((membership) => membership.organisationId);
		// A stored id from a previous account must not leak into the header.
		if (organisationId && owned.includes(organisationId)) return;
		setOrganisationId(data.activeOrganisationId ?? owned[0] ?? null);
	}, [data, organisationId, setOrganisationId]);

	if (session.isPending) {
		return (
			<div className="mx-auto flex w-full max-w-(--container-content) flex-col gap-4 p-6" aria-busy="true">
				<Skeleton className="h-8 w-48" label="Loading your account" />
				<Skeleton className="h-40 w-full" />
				<Skeleton className="h-40 w-full" />
			</div>
		);
	}

	if (session.isError) {
		if (isUnauthenticated(session.error)) {
			return <Navigate to={signInNavigateTarget(location.pathname, location.search)} replace />;
		}

		return (
			<div className="mx-auto w-full max-w-lg p-6">
				<ErrorPanel
					error={session.error}
					title="We could not load your account."
					onRetry={() => void session.refetch()}
					retryPending={session.isFetching}
				/>
			</div>
		);
	}

	if (!data) return null;

	if (
		data.user.mustChangePassword &&
		!location.pathname.startsWith("/user/settings/security") &&
		!location.pathname.startsWith("/manage/account/security") &&
		!location.pathname.startsWith("/sign-in")
	) {
		return <Navigate to="/user/settings/security?passwordChange=required" replace />;
	}

	const membership = activeMembership(data, organisationId);
	if (
		membership &&
		isMailOnlyOrganisationRole(membership.role) &&
		isBlockedForMailOnlyMember(location.pathname)
	) {
		window.location.replace(WEBMAIL_HOME);
		return null;
	}

	return <PlatformShell session={data} />;
}
